import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, ArrowRight } from '@dls/assets/icons';
import {
  Wrapper,
  CarouselWrapper,
  CarouselContainer,
  CarouselSlot,
  ArrowButton,
  ArrowButtonContainer,
  LeftArrowOverlay,
  RightArrowOverlay
} from './style';
import { testIdProps } from '../../helpers';
import PropTypes from 'prop-types';
import useDevice from '../../core/hooks/useDevice';

/**
 * This padding is used to give enough room to display the card shadow inside the carousel swimlane
 */
export const CARD_CAROUSEL_PADDING = 7;
/**
 * Gap between child items in the carousel swimlane
 */
const CAROUSEL_CHILD_ITEM_GAP = 16;
/**
 * Default width for carousel child item
 */
const DEFAULT_CAROUSEL_CHILD_WIDTH = 290;
/**
 * Display a preview of 40px of the next card in mobile breakpoint
 */
const CAROUSEL_NEXT_CHILD_PREVIEW_WIDTH_IN_MOBILE = 40;
/**
 * Default carousel children count for different breakpoints
 */
const DEFAULT_CAROUSEL_CHILDREN_COUNT = {
  sm: 2,
  md: 3,
  lg: 3,
  xl: 3
};

/**
 * In carousel scrollLeft value calculation, there is a rounding off error.
 * https://stackoverflow.com/questions/64502303/javascript-element-scrollleft-cant-handle-decimals
 * We need to factor in this when calculating carousel child width
 */
const CAROUSEL_SCROLL_LEFT_ROUNDING_OFF_ERROR_PX_VALUE = 1.5;

/**
 * Get fully visible child items count in the carousel container. These fully visible items would be
 * scrolled away when clicked on the arrow button.
 *
 * @param {number} currentIndex - Current index
 * @param {number} singleCarouselChildItemWidth - Single carousel child item width
 * @param {number} carouselContainerWidth - Carousel container width
 * @param {number} arrowOverlayWidth - Arrow overlay width
 * @returns Fully visible child items count
 */
const getFullyVisibleChildItemsCount = ({
  currentIndex,
  singleCarouselChildItemWidth,
  carouselContainerWidth,
  arrowOverlayWidth
}) => {
  let fullyVisibleItemsCount = 0;

  const calculateFullyVisibleItemsCount = carouselContainerWidthExcludingOverlay => {
    let carouselChildItemsWidthIncludingGapBetweenItems = 0;

    while (
      carouselContainerWidthExcludingOverlay >
      carouselChildItemsWidthIncludingGapBetweenItems
    ) {
      const carouselChildItemsWidthExcludingLastGap =
        carouselChildItemsWidthIncludingGapBetweenItems +
        singleCarouselChildItemWidth;

      if (
        carouselChildItemsWidthExcludingLastGap <
        carouselContainerWidthExcludingOverlay
      ) {
        fullyVisibleItemsCount++;
      }

      carouselChildItemsWidthIncludingGapBetweenItems =
        carouselChildItemsWidthExcludingLastGap + CAROUSEL_CHILD_ITEM_GAP;
    }
  };

  if (currentIndex <= 1) {
    const carouselContainerWidthExcludingRightOverlay =
      carouselContainerWidth - arrowOverlayWidth;
    calculateFullyVisibleItemsCount(
      carouselContainerWidthExcludingRightOverlay
    );
  } else {
    const carouselContainerWidthExcludingLeftAndRightOverlay =
      carouselContainerWidth - arrowOverlayWidth * 2 - CAROUSEL_CHILD_ITEM_GAP;
    calculateFullyVisibleItemsCount(
      carouselContainerWidthExcludingLeftAndRightOverlay
    );
  }

  return fullyVisibleItemsCount;
};

const CarouselSwimlane = ({
  type,
  children,
  swimlaneChildrenConfig,
  ...rest
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canNext, setCanNext] = useState(true);
  const [canPrev, setCanPrev] = useState(true);

  const carousel = useRef(null);
  const carouselChild = useRef(null);
  const leftArrowOverlayRef = useRef(null);

  /**
   * When user clicks right arrow, we store the left scroll value for that particular index in this object map.
   * So when user clicks left arrow later, we can easily set to the relevant scroll left value instead of calculating again.
   */
  const carouselLeftScrollValuesForNavigatedIndicesRef = useRef({});

  const {
    isMobile,
    isTablet,
    isLandscapeTablet,
    isDesktop,
    isTV
  } = useDevice();

  const numOfPage = children && children.length;

  const movePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prevState => prevState - 1);
    }
  };

  const moveNext = () => {
    const maxScrollWidth = carousel?.current?.scrollWidth;
    if (!canNext) return false;

    if (
      carousel.current !== null &&
      carousel.current.scrollLeft < maxScrollWidth
    ) {
      setCurrentIndex(prevState => prevState + 1);
    }
  };

  const determineSlideNextPossible = useCallback(
    carouselLeftScrollValue => {
      const maxScrollWidth = carousel?.current?.scrollWidth;
      const carouselContainerWidth = carousel?.current?.clientWidth;
      const totalScrolledWidthIncludingCarouselContainerWidth =
        carouselLeftScrollValue + carouselContainerWidth;

      const canNext =
        totalScrolledWidthIncludingCarouselContainerWidth < maxScrollWidth;
      setCanNext(canNext);
    },
    [carousel?.current?.scrollWidth]
  );

  const determineSlidePrevPossible = useCallback(() => {
    setCanPrev(currentIndex > 0);
  }, [currentIndex]);

  /**
   * Get arrow overlay width. Both left and right arrow are having same width (1 grid column width).
   * So, here we return the left arrow width. If element is not available in ref yet, we return 0.
   * @returns {number} - Arrow overlay width
   */
  const getArrowOverlayWidth = useCallback(() => {
    return leftArrowOverlayRef.current?.getBoundingClientRect().width || 0;
  }, []);

  /**
   * Scroll carousel content from left edge with the defined px value. When value is having decimals,
   * 'scrollLeft' property is ignoring the fractional point. Because of that, it is scrolled less than expected.
   * So here we round up to nearest integer and set.
   * https://stackoverflow.com/questions/64502303/javascript-element-scrollleft-cant-handle-decimals
   */
  const scrollCarousel = useCallback(carouselLeftScrollValue => {
    carousel.current.scrollLeft = Math.ceil(carouselLeftScrollValue);
  }, []);

  const onWheel = event => {
    if (event.deltaY) return;

    const spacings = 2;

    if (
      event.deltaX > 0 &&
      carousel.current.scrollLeft + spacings + carousel.current.clientWidth >
        carousel.current.scrollWidth
    ) {
      setCanNext(false);
      setCanPrev(true);
    } else if (event.deltaX < 0 && carousel.current.scrollLeft === 0) {
      setCanNext(true);
      setCanPrev(false);
    }
  };

  useEffect(() => {
    carousel.current.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      removeEventListener('wheel', onWheel);
    };
  }, []);

  useEffect(() => {
    // When screen size changed, reset the stored left scroll values for the indices and
    // slide to first item
    carouselLeftScrollValuesForNavigatedIndicesRef.current = {};

    // Reset overflow
    carousel.current.style.overflow = 'auto';
    setCurrentIndex(0);
  }, [isMobile, isTablet, isLandscapeTablet, isDesktop, isTV]);

  useEffect(() => {
    const carouselLeftScrollValueForCurrentIndex =
      carouselLeftScrollValuesForNavigatedIndicesRef.current[currentIndex];

    // If carousel left scrolling value for the current index is already stored in the object map,
    // use the stored value to scroll, instead of recalculating
    if (carouselLeftScrollValueForCurrentIndex) {
      scrollCarousel(carouselLeftScrollValueForCurrentIndex);

      determineSlideNextPossible(carouselLeftScrollValueForCurrentIndex);
      determineSlidePrevPossible();
      return;
    }

    const singleCarouselChildItemWidth = carouselChild?.current?.getBoundingClientRect()
      .width;
    const carouselContainerWidth = carousel?.current?.getBoundingClientRect()
      .width;
    const arrowOverlayWidth = getArrowOverlayWidth();

    let fullyVisibleItemsCount = getFullyVisibleChildItemsCount({
      currentIndex,
      singleCarouselChildItemWidth,
      carouselContainerWidth,
      arrowOverlayWidth
    });

    const carouselChildItemsWidth =
      fullyVisibleItemsCount * singleCarouselChildItemWidth;
    const carouselChildItemsGap =
      fullyVisibleItemsCount * CAROUSEL_CHILD_ITEM_GAP;
    const carouselChildItemsWidthAndGap =
      carouselChildItemsWidth + carouselChildItemsGap;

    if (carousel !== null && carousel.current !== null) {
      let carouselLeftScrollValue = 0;
      switch (currentIndex) {
        case 0:
          carouselLeftScrollValue = 0;
          break;
        case 1:
          carouselLeftScrollValue =
            carouselChildItemsWidthAndGap -
            arrowOverlayWidth -
            CAROUSEL_CHILD_ITEM_GAP +
            CARD_CAROUSEL_PADDING;
          break;
        default:
          carouselLeftScrollValue =
            carousel.current.scrollLeft + carouselChildItemsWidthAndGap;
          break;
      }

      scrollCarousel(carouselLeftScrollValue);

      // Store carousel left scrolling value for the current index
      const updatedLeftScrollValuesObjMap = {
        ...carouselLeftScrollValuesForNavigatedIndicesRef.current
      };

      updatedLeftScrollValuesObjMap[currentIndex] = carouselLeftScrollValue;
      carouselLeftScrollValuesForNavigatedIndicesRef.current = updatedLeftScrollValuesObjMap;

      determineSlideNextPossible(carouselLeftScrollValue);
      determineSlidePrevPossible();
    }
  }, [
    currentIndex,
    determineSlidePrevPossible,
    determineSlideNextPossible,
    getArrowOverlayWidth,
    scrollCarousel
  ]);

  const getCarouselSlotStyle = () => {
    const carouselSlotStyle = {};

    const carouselContainerWidth =
      carousel.current?.getBoundingClientRect().width || 0;
    let childWidth = DEFAULT_CAROUSEL_CHILD_WIDTH;

    if (!isMobile) {
      let childrenCount = DEFAULT_CAROUSEL_CHILDREN_COUNT.lg;
      if (isTablet) {
        childrenCount =
          swimlaneChildrenConfig?.sm || DEFAULT_CAROUSEL_CHILDREN_COUNT.sm;
      } else if (isLandscapeTablet) {
        childrenCount =
          swimlaneChildrenConfig?.md || DEFAULT_CAROUSEL_CHILDREN_COUNT.md;
      } else if (isDesktop) {
        childrenCount =
          swimlaneChildrenConfig?.lg || DEFAULT_CAROUSEL_CHILDREN_COUNT.lg;
      } else {
        childrenCount =
          swimlaneChildrenConfig?.xl || DEFAULT_CAROUSEL_CHILDREN_COUNT.xl;
      }

      const arrowOverlayWidth = getArrowOverlayWidth();

      let carouselContainerWidthExcludingLeftAndRightOverlay = 0;
      if (arrowOverlayWidth > 0 && carouselContainerWidth > 0) {
        carouselContainerWidthExcludingLeftAndRightOverlay =
          carouselContainerWidth -
          (arrowOverlayWidth + CAROUSEL_CHILD_ITEM_GAP) * 2;
      }

      if (carouselContainerWidthExcludingLeftAndRightOverlay > 0) {
        const gapBetweenChildren =
          CAROUSEL_CHILD_ITEM_GAP * (childrenCount - 1);

        const childrenContainerWidthExcludingGapBetweenChildren =
          carouselContainerWidthExcludingLeftAndRightOverlay +
          CAROUSEL_SCROLL_LEFT_ROUNDING_OFF_ERROR_PX_VALUE -
          gapBetweenChildren;

        childWidth =
          childrenContainerWidthExcludingGapBetweenChildren / childrenCount;
      }
    } else {
      if (carouselContainerWidth > 0) {
        childWidth =
          carouselContainerWidth -
          CAROUSEL_CHILD_ITEM_GAP -
          CAROUSEL_NEXT_CHILD_PREVIEW_WIDTH_IN_MOBILE;
      }
    }

    carouselSlotStyle.width = childWidth;
    return carouselSlotStyle;
  };

  const renderChildren = type => {
    const carouselChildren = React.Children.toArray(children);
    let renderedChildren;
    if (carouselChildren.length) {
      renderedChildren = carouselChildren;

      const carouselSlotStyle = getCarouselSlotStyle();

      return renderedChildren.map((child, index) => {
        const props = {};
        if (index === 0) props.ref = carouselChild;
        return (
          <CarouselSlot
            key={index}
            type={type}
            {...props}
            style={{ ...carouselSlotStyle }}
          >
            {child}
          </CarouselSlot>
        );
      });
    }
    return null;
  };

  const showLeftArrow = canPrev && numOfPage > 1;
  const showRightArrow = canNext && numOfPage > 1;

  return (
    <div {...testIdProps(rest)}>
      <Wrapper>
        <LeftArrowOverlay
          xs={1}
          ref={leftArrowOverlayRef}
          showArrow={showLeftArrow}
        >
          <ArrowButtonContainer>
            <ArrowButton
              data-testid="arrow-left"
              onClick={movePrev}
              inside
              showArrow={showLeftArrow}
              position="relative"
            >
              <ArrowLeft size={25} />
            </ArrowButton>
          </ArrowButtonContainer>
        </LeftArrowOverlay>

        <CarouselWrapper
          type={type}
          ref={carousel}
          data-testid="carousel-container-wrapper"
        >
          <CarouselContainer data-testid="carousel-container" type={type}>
            {renderChildren(type)}
          </CarouselContainer>
        </CarouselWrapper>

        <RightArrowOverlay xs={1} showArrow={showRightArrow}>
          <ArrowButtonContainer>
            <ArrowButton
              data-testid="arrow-right"
              onClick={moveNext}
              inside
              showArrow={showRightArrow}
              position="relative"
            >
              <ArrowRight size={25} />
            </ArrowButton>
          </ArrowButtonContainer>
        </RightArrowOverlay>
      </Wrapper>
    </div>
  );
};

CarouselSwimlane.defaultProps = {
  swimlaneChildrenConfig: {
    sm: 2,
    md: 3,
    lg: 3,
    xl: 3
  }
};

CarouselSwimlane.displayName = 'CarouselSwimlane';

CarouselSwimlane.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // |     To update them edit the d.ts file and run "yarn proptypes"     |
  // ----------------------------------------------------------------------
  /**
   * carousel items
   */
  children: PropTypes.any.isRequired,
  /**
   * Configure for different breakpoints, how many children to be fully visible in swimlane when both
   * left and right arrow overlays are shown.
   * @default {sm: 2, md: 3, lg: 3, xl: 3}
   */
  swimlaneChildrenConfig: PropTypes.shape({
    /**
     * Configure for lg breakpoint, how many children to be fully visible when both
     * left and right arrow overlays are shown
     * @default 3
     */
    lg: PropTypes.number.isRequired,
    /**
     * Configure for md breakpoint, how many children to be fully visible when both
     * left and right arrow overlays are shown
     * @default 3
     */
    md: PropTypes.number.isRequired,
    /**
     * Configure for sm breakpoint, how many children to be fully visible when both
     * left and right arrow overlays are shown
     * @default 2
     */
    sm: PropTypes.number.isRequired,
    /**
     * Configure for xl breakpoint, how many children to be fully visible when both
     * left and right arrow overlays are shown
     * @default 3
     */
    xl: PropTypes.number.isRequired
  }).isRequired
};

export default CarouselSwimlane;
